// =============================================
// handwrittenDate — the date someone wrote on a document by hand
// =============================================
// Many scanned receipts and invoices carry a date added in pen. The rule (set by the
// account owner, 2026-10-09):
//
//   * The handwritten date is usually the BOOKKEEPING date, so it wins over the
//     printed one — it is the date that gets filled in.
//   * It can also be a slip (typically last year's number written in the first days
//     of a new year). So when it DIFFERS from the printed date it is still filled in,
//     but the user must confirm which date is right before the document is filed.
//
// Handwriting cannot be read by the free in-browser reader. `readHandwrittenDate`
// sends a picture of the first page (plus a sharp close-up of its top) to the server's AI reader
// (POST /api/extract/handwritten-date) — one short call per document, asked once and
// then kept on the document (dms_documents.handwritten_checked, migration 121). It is
// skipped entirely when the user chose the free engine.
// =============================================

import apiClient from '../../../lib/api/apiClient';
import { renderPdfFirstPageImages } from './pdfTextExtractor';

/** Where the filled-in document date came from (dms_documents.date_source). */
export type DateSource =
  | 'printed' | 'handwritten' | 'filename'                       // auto-filled
  | 'handwritten-confirmed' | 'printed-confirmed' | 'user';      // settled by a person

export interface DateDecision {
  /** the date to fill in, YYYY-MM-DD ('' = none found) */
  date: string;
  source: DateSource | null;
  /** handwritten and printed dates disagree — a person has to say which is right */
  confirmNeeded: boolean;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const iso = (v: unknown): string => {
  const s = String(v || '').slice(0, 10);
  return ISO.test(s) ? s : '';
};

/**
 * Which date to fill in, given the printed date the auto-read found and the handwritten
 * one (either may be missing). Pure — see the rule at the top of this file.
 */
export function decideDocDate(
  printedDate: string | null | undefined,
  handwrittenDate: string | null | undefined,
  printedFrom: 'text' | 'filename' = 'text',
): DateDecision {
  const printed = iso(printedDate);
  const handwritten = iso(handwrittenDate);
  const printedSource: DateSource = printedFrom === 'filename' ? 'filename' : 'printed';
  if (!handwritten) return { date: printed, source: printed ? printedSource : null, confirmNeeded: false };
  if (!printed) return { date: handwritten, source: 'handwritten', confirmNeeded: false };
  if (printed === handwritten) return { date: printed, source: printedSource, confirmNeeded: false };
  // A date taken from the FILE NAME is the user's own label, not something printed on
  // the page — it is no reason to question what they wrote on the document.
  if (printedFrom === 'filename') return { date: handwritten, source: 'handwritten', confirmNeeded: false };
  return { date: handwritten, source: 'handwritten', confirmNeeded: true };
}

/** Has a person already settled this document's date? (then nothing is asked again) */
export function isDateSettled(dateSource: string | null | undefined): boolean {
  return dateSource === 'handwritten-confirmed' || dateSource === 'printed-confirmed' || dateSource === 'user';
}

/** The user's auto-read engine choice (Upload Docs). 'local' = free only, no AI calls. */
export function handwritingCheckAllowed(engine?: string | null): boolean {
  let chosen = engine;
  if (!chosen) {
    try { chosen = localStorage.getItem('akt_doc_extract_engine'); } catch { chosen = null; }
  }
  return chosen !== 'local';
}

export interface HandwrittenRead {
  /** YYYY-MM-DD, '' when the page has no (legible) handwritten date */
  date: string;
  /** as written, e.g. "21/7/21" */
  raw: string;
}

/**
 * Look at a document's first page for a handwritten date. Resolves with the date (or
 * date '' when there is none); resolves NULL when the check could not be made at all
 * (offline, AI not available, page could not be drawn) — the caller then leaves the
 * document unchecked so it is asked again another time.
 */
export async function readHandwrittenDate(gzipBase64: string): Promise<HandwrittenRead | null> {
  try {
    const images = await renderPdfFirstPageImages(gzipBase64);
    if (!images) return null;
    const resp = await apiClient.post<{ found?: boolean; handwritten_date?: string; raw?: string; devMock?: boolean }>(
      '/extract/handwritten-date', { imageBase64: images.page, topImageBase64: images.top || undefined, mediaType: 'image/jpeg' },
    );
    const d = resp.data;
    if (!d || d.devMock) return null;        // no AI key on this server: nothing was checked
    const date = d.found ? iso(d.handwritten_date) : '';
    return { date, raw: date ? String(d.raw || '') : '' };
  } catch {
    return null;
  }
}
